import { MerrySidePopup } from "@/shared/ui/MerrySidePopup";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

import borderDark from "@/assets/figma/left-navigation/border-dark.svg";
import borderLight from "@/assets/figma/left-navigation/border-light.svg";
import chatHistory from "@/assets/figma/left-navigation/chat-history.svg";
import dataOverview from "@/assets/figma/left-navigation/data-overview.svg";
import divider from "@/assets/figma/left-navigation/divider.svg";
import home from "@/assets/figma/left-navigation/home.svg";
import myFiles from "@/assets/figma/left-navigation/my-files.svg";
import newChat from "@/assets/figma/left-navigation/new-chat.svg";
import notification from "@/assets/figma/left-navigation/notification.svg";
import { cn } from "@/shared/lib/cn";
import { MerryButton } from "@/shared/ui/MerryButton";
import { MerryChatLabel } from "@/shared/ui/MerryChatLabel";
import {
  getMerryChatIndicatorAccessibleLabel,
  type MerryChatNavigationIndicator,
} from "@/shared/ui/merryChatActivityModel";
import {
  isMerryLeftNavigationRouteItem,
  merryLeftNavigationDividerNodeIds,
  merryLeftNavigationSections,
  type MerryLeftNavigationItemId,
  type MerryLeftNavigationRouteItemId,
} from "@/shared/ui/merryLeftNavigationItems";

gsap.registerPlugin(useGSAP);

const iconSources: Record<MerryLeftNavigationItemId, string> = {
  newChat,
  chatHistory,
  home,
  dataOverview,
  myFiles,
  notifications: notification,
};

export interface MerryLeftNavigationProps {
  "data-testid"?: string;
  activeItem: MerryLeftNavigationRouteItemId;
  ariaLabel?: string;
  chatHistoryDrawer?: ReactNode;
  chatHistoryIndicator?: MerryChatNavigationIndicator;
  notificationIndicator?: MerryChatNavigationIndicator;
  notificationDrawer?: ReactNode;
  onNotificationOpen?: () => void;
  otherDrawerOpen?: boolean;
  navigationKey?: string;
  notificationOpen?: boolean;
  onNotificationToggle?: () => void;
  className?: string;
  itemTestIdPrefix?: string;
  onNavigate: (item: MerryLeftNavigationRouteItemId) => void;
  onNewChat: () => void;
}

export function MerryLeftNavigation({
  "data-testid": testId,
  activeItem,
  ariaLabel = "登录后主导航",
  chatHistoryDrawer,
  chatHistoryIndicator = { type: "none" },
  notificationIndicator = { type: "none" },
  notificationDrawer,
  onNotificationOpen,
  otherDrawerOpen = false,
  navigationKey,
  notificationOpen,
  onNotificationToggle,
  className,
  itemTestIdPrefix = "left-navigation-item",
  onNavigate,
  onNewChat,
}: MerryLeftNavigationProps) {
  const chatHistoryItemRef = useRef<HTMLDivElement>(null);
  const drawerId = useId();
  const [isChatHistoryDrawerOpen, setIsChatHistoryDrawerOpen] = useState(false);
  const [localNotificationOpen, setLocalNotificationOpen] = useState(false);
  const isNotificationDrawerOpen = notificationOpen ?? localNotificationOpen;
  const notificationItemRef = useRef<HTMLDivElement>(null);
  const notificationDrawerId = useId();
  const preserveNotificationOnNextNavigation = useRef(false);

  const closeChatHistoryDrawerAndRestoreFocus = useCallback(() => {
    setIsChatHistoryDrawerOpen(false);
    const trigger = chatHistoryItemRef.current?.querySelector<HTMLButtonElement>(
      "button",
    );
    if (trigger && document.activeElement !== trigger) {
      trigger.focus();
    }
  }, []);

  useEffect(() => {
    if (!otherDrawerOpen) return;
    const timer = window.setTimeout(() => {
      setLocalNotificationOpen(false);
      setIsChatHistoryDrawerOpen(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [otherDrawerOpen]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (preserveNotificationOnNextNavigation.current) {
        preserveNotificationOnNextNavigation.current = false;
        return;
      }
      setLocalNotificationOpen(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [navigationKey]);

  useEffect(() => {
    if (!isChatHistoryDrawerOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeChatHistoryDrawerAndRestoreFocus();
    };

    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [closeChatHistoryDrawerAndRestoreFocus, isChatHistoryDrawerOpen]);

  useEffect(() => {
    if (!isChatHistoryDrawerOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!chatHistoryItemRef.current?.contains(event.target as Node)) setIsChatHistoryDrawerOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [isChatHistoryDrawerOpen]);

  useEffect(() => {
    if (!isNotificationDrawerOpen) return;
    const close = (event: PointerEvent) => { if (!notificationItemRef.current?.contains(event.target as Node)) { if (onNotificationToggle) onNotificationToggle(); else setLocalNotificationOpen(false); } };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { if (onNotificationToggle) onNotificationToggle(); else setLocalNotificationOpen(false); notificationItemRef.current?.querySelector<HTMLButtonElement>("button")?.focus(); } };
    document.addEventListener("pointerdown", close); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, [isNotificationDrawerOpen, onNotificationToggle]);

  useGSAP(
    () => {
      const drawer = chatHistoryItemRef.current?.querySelector(".merry-left-navigation__chat-drawer")?.firstElementChild;
      if (!(drawer instanceof HTMLElement)) return;

      gsap.killTweensOf(drawer);
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(drawer, {
          autoAlpha: isChatHistoryDrawerOpen ? 1 : 0,
          x: 0,
        });
        return;
      }

      gsap.to(drawer, {
        autoAlpha: isChatHistoryDrawerOpen ? 1 : 0,
        duration: isChatHistoryDrawerOpen ? 0.28 : 0.18,
        ease: isChatHistoryDrawerOpen ? "power3.out" : "power2.in",
        overwrite: true,
        x: isChatHistoryDrawerOpen ? 0 : -12,
      });
    },
    {
      dependencies: [isChatHistoryDrawerOpen],
      scope: chatHistoryItemRef,
    },
  );

  return (
    <nav
      aria-label={ariaLabel}
      className={cn("merry-left-navigation", className)}
      data-figma-node-id="299:31085"
      data-testid={testId}
    >
      <span aria-hidden="true" className="merry-left-navigation__edge">
        <img
          alt=""
          className="merry-left-navigation__edge-line merry-left-navigation__edge-line--light"
          src={borderLight}
        />
        <img
          alt=""
          className="merry-left-navigation__edge-line merry-left-navigation__edge-line--dark"
          src={borderDark}
        />
      </span>

      {merryLeftNavigationSections.map((section, sectionIndex) => (
        <Fragment key={section[0].id}>
          {section.map((item) => {
            const isRoute = isMerryLeftNavigationRouteItem(item);
            const isActive = isRoute && item.id === activeItem;

            const isChatHistory = item.id === "chatHistory";
            const itemIndicator = isChatHistory
              ? chatHistoryIndicator
              : item.id === "notifications"
                ? notificationIndicator
                : { type: "none" as const };
            const statusLabel =
              item.id === "notifications" && itemIndicator.type === "count"
                ? `有 ${itemIndicator.count} 条未读通知`
                : item.id === "notifications" && itemIndicator.type === "dot"
                  ? "有未读通知"
                  : isChatHistory
                    ? getMerryChatIndicatorAccessibleLabel(itemIndicator)
                    : null;

            return (
              <div
                className="merry-left-navigation__item"
                data-figma-node-id={item.figmaNodeId}
                key={item.id}
                ref={isChatHistory ? chatHistoryItemRef : item.id === "notifications" ? notificationItemRef : undefined}
              >
                <MerryButton
                  aria-current={isActive && item.id !== "notifications" ? "page" : undefined}
                  aria-controls={
                    isChatHistory && chatHistoryDrawer ? drawerId : item.id === "notifications" && notificationDrawer ? notificationDrawerId : undefined
                  }
                  aria-expanded={
                    isChatHistory && chatHistoryDrawer
                      ? isChatHistoryDrawerOpen
                      : item.id === "notifications" && notificationDrawer ? isNotificationDrawerOpen : undefined
                  }
                  aria-label={
                    statusLabel ? `${item.label}，${statusLabel}` : item.label
                  }
                  className="merry-left-navigation__button"
                  data-left-navigation-active={isActive ? "true" : "false"}
                  data-left-navigation-item={item.id}
                  data-testid={`${itemTestIdPrefix}-${item.id}`}
                  hierarchy="navigation"
                  icon={
                    <img
                      alt=""
                      aria-hidden="true"
                      className={cn(
                        "merry-left-navigation__icon",
                        `merry-left-navigation__icon--${item.id}`,
                      )}
                      src={iconSources[item.id]}
                    />
                  }
                  onMouseDown={item.id === "newChat" || item.id === "chatHistory" ? event => event.preventDefault() : undefined}
                  onPress={() => {
                    if (item.id === "chatHistory" && chatHistoryDrawer) {
                      setIsChatHistoryDrawerOpen(open => !open);
                      if (isNotificationDrawerOpen) {
                        if (onNotificationToggle) onNotificationToggle();
                        else setLocalNotificationOpen(false);
                      }
                      return;
                    }
                    if (item.id === "notifications" && notificationDrawer) {
                      if (onNotificationToggle) { onNotificationToggle(); setIsChatHistoryDrawerOpen(false); return; }
                      if (!isNotificationDrawerOpen) {
                        preserveNotificationOnNextNavigation.current = otherDrawerOpen;
                        onNotificationOpen?.();
                      }
                      setLocalNotificationOpen(open => !open);
                      setIsChatHistoryDrawerOpen(false);
                      return;
                    }
                    setLocalNotificationOpen(false);
                    if (isRoute) {
                      onNavigate(item.id);
                    } else {
                      onNewChat();
                    }
                  }}
                  size={48}
                />

                {isChatHistory && chatHistoryIndicator.type !== "none" ? (
                  <span className="merry-left-navigation__chat-indicator">
                    {chatHistoryIndicator.type === "count" ? (
                      <MerryChatLabel
                        count={chatHistoryIndicator.count}
                        size={14}
                        type="count"
                      />
                    ) : (
                      <MerryChatLabel
                        size={14}
                        type={chatHistoryIndicator.type}
                      />
                    )}
                  </span>
                ) : null}

                {item.id === "notifications" &&
                notificationIndicator.type !== "none" ? (
                  <span className="merry-left-navigation__chat-indicator">
                    {notificationIndicator.type === "count" ? (
                      <MerryChatLabel
                        count={notificationIndicator.count}
                        size={14}
                        type="count"
                      />
                    ) : (
                      <MerryChatLabel
                        size={14}
                        type={notificationIndicator.type}
                      />
                    )}
                  </span>
                ) : null}

                {item.id === "notifications" && notificationDrawer ? <MerrySidePopup id={notificationDrawerId} className="merry-left-navigation__notification-drawer" label="通知" open={isNotificationDrawerOpen} width="compact" anchorRef={notificationItemRef} anchorAlignment="bottom">{notificationDrawer}</MerrySidePopup> : null}

                {isChatHistory && chatHistoryDrawer ? (
                  <MerrySidePopup
                    className="merry-left-navigation__chat-drawer"
                    label="聊天历史"
                    open={isChatHistoryDrawerOpen}
                    width="wide"
                    id={drawerId}
                    anchorRef={chatHistoryItemRef}
                    anchorAlignment="top"
                  >
                    {chatHistoryDrawer}
                  </MerrySidePopup>
                ) : null}
              </div>
            );
          })}

          {sectionIndex < merryLeftNavigationDividerNodeIds.length ? (
            <span
              aria-hidden="true"
              className="merry-left-navigation__divider"
              data-figma-node-id={
                merryLeftNavigationDividerNodeIds[sectionIndex]
              }
            >
              <img alt="" src={divider} />
            </span>
          ) : null}
        </Fragment>
      ))}
    </nav>
  );
}
